import { SETTINGS_TAB } from './SettingsMenu';

export type ProductModule = 'marketing' | 'sales';

const DEFAULT_PRODUCT_MODULES: ProductModule[] = ['marketing', 'sales'];

interface SettingsTabAccessOptions {
  // Retained for caller compatibility; tab visibility no longer depends on it
  // (plan-tier gating for Champions is handled by the PlanGate around the tab).
  isEnterprise?: boolean;
  productModules?: ProductModule[] | null;
}

export const getEnabledProductModules = (
  productModules?: ProductModule[] | null,
): ProductModule[] => {
  if (!Array.isArray(productModules) || productModules.length === 0) {
    return DEFAULT_PRODUCT_MODULES;
  }

  return productModules;
};

export const canAccessSettingsTab = (
  tab: SETTINGS_TAB,
  { productModules }: SettingsTabAccessOptions,
): boolean => {
  const enabledModules = getEnabledProductModules(productModules);
  const hasMarketing = enabledModules.includes('marketing');
  const hasSales = enabledModules.includes('sales');

  switch (tab) {
    case SETTINGS_TAB.CHAMPIONS:
      // Champion Tracking is Growth+ (FEATURE_MIN_PLAN.champion_tracking, enforced by the
      // PlanGate around the tab). Show it to any sales workspace and let PlanGate gate
      // access on click — previously this hid it at non-enterprise, contradicting the
      // Growth-tier PlanGate and leaving a direct-URL bypass.
      return hasSales;
    case SETTINGS_TAB.OUTREACH:
    case SETTINGS_TAB.EMAIL_UNSUBSCRIBES:
      return hasSales;
    case SETTINGS_TAB.BILLING_AND_USAGE:
      // Both sales and marketing modules support self-serve billing.
      return hasSales || hasMarketing;
    case SETTINGS_TAB.MB_INTENT_CLIENT:
      return hasMarketing;
    case SETTINGS_TAB.EMAIL_DIGEST:
      return hasSales || hasMarketing;
    case SETTINGS_TAB.NOTIFICATIONS:
      // Proactive Pounce notifications are a sales-outreach feature, same gate as Channels.
      return hasSales;
    default:
      return true;
  }
};

/**
 * Module-only access check (ignores plan tier).
 * Used for direct-URL redirect so plan-gated tabs (e.g., Champions for non-enterprise)
 * still reach their PlanGate upgrade UI instead of bouncing to Profile.
 */
export const isTabAllowedForModule = (
  tab: SETTINGS_TAB,
  productModules?: ProductModule[] | null,
): boolean => {
  const enabledModules = getEnabledProductModules(productModules);
  const hasMarketing = enabledModules.includes('marketing');
  const hasSales = enabledModules.includes('sales');

  switch (tab) {
    case SETTINGS_TAB.BILLING_AND_USAGE:
      return hasSales || hasMarketing;
    case SETTINGS_TAB.CHAMPIONS:
    case SETTINGS_TAB.OUTREACH:
    case SETTINGS_TAB.EMAIL_UNSUBSCRIBES:
      return hasSales;
    case SETTINGS_TAB.MB_INTENT_CLIENT:
      return hasMarketing;
    case SETTINGS_TAB.EMAIL_DIGEST:
      return hasSales || hasMarketing;
    case SETTINGS_TAB.NOTIFICATIONS:
      return hasSales;
    default:
      return true;
  }
};
